import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { phase1Api } from "../api/client";
import LogoMark from "../components/LogoMark";


export default function PaymentReturn() {
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get("payment");
  const [checking, setChecking] = useState(true);
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState("");
  const [automaticChecks, setAutomaticChecks] = useState(0);

  const checkPayment = useCallback(async () => {
    if (!paymentId) {
      setError("We could not identify the payment. Open your orders to check its status.");
      setChecking(false);
      return;
    }
    setChecking(true);
    setError("");
    try {
      const response = await phase1Api.checkPaynowPayment(paymentId);
      setPayment(response.data || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "We could not confirm the payment yet. Please try checking again.");
    } finally {
      setChecking(false);
    }
  }, [paymentId]);

  useEffect(() => {
    checkPayment();
  }, [checkPayment]);

  const confirmed = payment?.status === "CONFIRMED" || payment?.order_payment_status === "PAID";
  const unsuccessful = ["FAILED", "CANCELLED"].includes(payment?.status);
  const isSubscription = payment?.payment_for === "SUBSCRIPTION";
  const isDelivery = payment?.payment_for === "DELIVERY";
  const destination = isSubscription ? "/portal/subscriptions" : isDelivery ? "/portal/orders" : "/portal/orders";

  useEffect(() => {
    if (checking || !payment || confirmed || unsuccessful || automaticChecks >= 6) return undefined;
    const timer = window.setTimeout(() => {
      setAutomaticChecks((count) => count + 1);
      checkPayment();
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [automaticChecks, checkPayment, checking, confirmed, payment, unsuccessful]);

  return (
    <main className="payment-return-page">
      <section className="payment-return-card" aria-live="polite">
        <LogoMark />
        {checking ? (
          <>
            <span className="payment-return-icon is-checking">…</span>
            <h1>Checking your payment</h1>
            <p>Please wait while TAZY CART confirms the latest payment status.</p>
          </>
        ) : confirmed ? (
          <>
            <span className="payment-return-icon is-success">✓</span>
            <h1>Payment confirmed</h1>
            <p>Your payment{payment?.code ? ` ${payment.code}` : ""} was successful and {isSubscription ? "your package is now active." : isDelivery ? "the transporter can now proceed with pickup." : "your order has been updated."}</p>
          </>
        ) : unsuccessful ? (
          <>
            <span className="payment-return-icon is-error">×</span>
            <h1>Payment was not completed</h1>
            <p>No confirmed payment was recorded. You can return to {isSubscription ? "your package" : "your order"} and try again.</p>
          </>
        ) : (
          <>
            <span className="payment-return-icon is-pending">…</span>
            <h1>Payment confirmation pending</h1>
            <p>{error || "Your payment has not been confirmed yet. You can check again safely."}</p>
          </>
        )}

        {error && !checking && !unsuccessful && <div className="payment-return-message">{error}</div>}
        <div className="payment-return-actions">
          {!confirmed && !checking && <button type="button" onClick={checkPayment}>Check again</button>}
          <Link to={destination}>{isSubscription ? "View my package" : "View my orders"}</Link>
        </div>
      </section>
    </main>
  );
}
